import React, { Fragment, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  selectBudgetRecipient,
  selectCreditCard,
  selectCreditCardIdFromRoute,
  selectCreditCardPermissions,
  selectErrorDetailMapFromState,
  selectFilteredRecentUserEmailOptions,
  selectFilteredUserEmailOptions,
  selectPuttingCreditCardFromState,
  selectTopLevelErrorFromState,
  selectUsersFullPaginatedData,
} from 'src/app/UpdateBudget/redux/updateBudget.selectors'
import NotFoundRightFlyoutContent from 'src/components/NotFoundRightFlyoutContent/NotFoundRightFlyoutContent'
import RightFlyout, {
  RightFlyoutBodyWidths,
} from 'src/components/RightFlyout/RightFlyout'
import UpdateBudgetRecurringContent from 'src/components/UpdateBudgetRecurringContent/UpdateBudgetRecurringContent'
import UpdateBudgetStandardContent from 'src/components/UpdateBudgetStandardContent/UpdateBudgetStandardContent'
import { UserDropdownInfiniteScrollProps } from 'src/components/UserDropdown/UserDropdown'
import { useFlyoutDispatch } from 'src/hooks'
import { useIsUserOrglessRCNUser } from 'src/hooks/useOrglessUserStatus'
import { createSetIsSetupOrganizationPopupOpen } from 'src/redux/actions/app.actions'
import {
  getCreditCard,
  getCreditCardPermissions,
  getParentCreditCard,
  putCreditCard,
} from 'src/redux/actions/data/creditCards.actions'
import { getUsers } from 'src/redux/actions/data/users.actions'
import { selectIsConfirmBudgetModalUpdateBudgetDismissed } from 'src/redux/selectors/auth.selectors'
import { PutCreditCardRequest } from 'src/requests/putCreditCard.request'

export enum UpdateBudgetScreen {
  Standard,
  Recurring,
}

const UpdateBudget: React.FC = () => {
  const id = useSelector(selectCreditCardIdFromRoute)
  const creditCard = useSelector(selectCreditCard)
  const creditCardPermissions = useSelector(selectCreditCardPermissions)
  const recipient = useSelector(selectBudgetRecipient)
  const puttingCreditCard = useSelector(selectPuttingCreditCardFromState)
  const topLevelError = useSelector(selectTopLevelErrorFromState)
  const errorDetailMap = useSelector(selectErrorDetailMapFromState)
  const usersFullPaginatedData = useSelector(selectUsersFullPaginatedData)
  const filteredUserEmailOptions = useSelector(selectFilteredUserEmailOptions)
  const filteredRecentUserEmailOptions = useSelector(
    selectFilteredRecentUserEmailOptions,
  )
  // Fetches CreditCardsSourceCount itself; true only after a successful
  // load, subsuming the legacy statisticsLoaded gate.
  const isUserOrglessRCNUser = useIsUserOrglessRCNUser()
  const isConfirmBudgetModalDismissed = useSelector(
    selectIsConfirmBudgetModalUpdateBudgetDismissed,
  )

  const dispatch = useFlyoutDispatch()

  useEffect(() => {
    dispatch(getCreditCard(id))
    dispatch(getCreditCardPermissions(id))
  }, [id])

  useEffect((): void => {
    if (
      creditCardPermissions?.canUpdateCreditCard &&
      creditCard?.parentCreditCardId !== undefined
    ) {
      dispatch(getParentCreditCard(creditCard?.parentCreditCardId))
    }
  }, [creditCardPermissions, creditCard?.parentCreditCardId])

  const dispatchPutCreditCard = (body: Partial<PutCreditCardRequest>): void => {
    dispatch(putCreditCard(id, body, true))
  }

  useEffect(() => {
    if (isUserOrglessRCNUser && !isConfirmBudgetModalDismissed) {
      dispatch(createSetIsSetupOrganizationPopupOpen(true))
    }
  }, [isUserOrglessRCNUser])

  const currentScreen = creditCard?.recurs
    ? UpdateBudgetScreen.Recurring
    : UpdateBudgetScreen.Standard
  const [screen, setScreen] = useState(currentScreen)
  useEffect((): void => {
    setScreen(currentScreen)
  }, [currentScreen])

  const fetchUsers = (filterInput?: string, page?: number): void => {
    dispatch(
      getUsers(
        {
          q: filterInput,
          count: 10,
          page: page,
        },
        true,
      ),
    )
  }

  const userInfiniteScrollProps: UserDropdownInfiniteScrollProps = {
    fetch: fetchUsers,
    ...usersFullPaginatedData,
  }

  return (
    <RightFlyout width={RightFlyoutBodyWidths.Action}>
      {creditCard !== undefined &&
      recipient !== undefined &&
      creditCardPermissions?.canUpdateCreditCard ? (
        <Fragment>
          {screen === UpdateBudgetScreen.Standard && (
            <UpdateBudgetStandardContent
              creditCard={creditCard}
              recipient={recipient}
              userOptions={filteredUserEmailOptions}
              recentUserOptions={filteredRecentUserEmailOptions}
              rcnOptions={[]}
              puttingCreditCard={puttingCreditCard}
              putCreditCard={dispatchPutCreditCard}
              setScreen={setScreen}
              topLevelError={topLevelError}
              errorDetailMap={errorDetailMap}
              userInfiniteScrollProps={userInfiniteScrollProps}
            />
          )}
          {screen === UpdateBudgetScreen.Recurring && (
            <UpdateBudgetRecurringContent
              creditCard={creditCard}
              recipient={recipient}
              userOptions={filteredUserEmailOptions}
              recentUserOptions={filteredRecentUserEmailOptions}
              rcnOptions={[]}
              puttingCreditCard={puttingCreditCard}
              putCreditCard={dispatchPutCreditCard}
              setScreen={setScreen}
              topLevelError={topLevelError}
              errorDetailMap={errorDetailMap}
              userInfiniteScrollProps={userInfiniteScrollProps}
            />
          )}
        </Fragment>
      ) : (
        <NotFoundRightFlyoutContent />
      )}
    </RightFlyout>
  )
}

export default UpdateBudget
